<template>
	<div class="Toolbar" :class="classes">
		<!-- @slot Toolbar group -->
		<slot />
	</div>
</template>

<script>
import CloToolbarGroup from '@/navigation/CloToolbar/CloToolbarGroup.vue';
import CloToolbarItem from '@/navigation/CloToolbar/CloToolbarItem.vue';

export default {
	components: { CloToolbarGroup, CloToolbarItem },
	data() {
		return {};
	},
	props: {
		reverse: Boolean,
		/**
		 * Color (only available with reverse): primary, neutral, primaryDark, neutralDark
		 */
		color: String,
		/**
		 * Fixed to top of viewport
		 */
		fixed: Boolean,
		/**
		 * Split: First group is fixed to top, other groups are fixed to bottom
		 */
		split: Boolean,
	},
	computed: {
		classes: function () {
			switch (this.color) {
				case 'primary':
					return this.reverse ? 'Toolbar--reversePrimary' : '';
					break;
				case 'neutral':
					return this.reverse ? 'Toolbar--reverseNeutral' : '';
					break;
				case 'primaryDark':
					return this.reverse ? 'Toolbar--reversePrimaryDark' : '';
					break;
				case 'neutralDark':
					return this.reverse ? 'Toolbar--reverseNeutralDark' : '';
					break;
				default:
					return this.reverse ? 'Toolbar--reverse' : '';
					break;
			}
		},
	},
};
</script>

<style lang="scss">
/* ------------------------------------------------------------------------------
	Toolbar component
------------------------------------------------------------------------------ */

.Toolbar {
	position: relative;
	width: 100%;
	display: flex;
	align-items: stretch;
	@include text(3);
	background-color: $brand-color-sky-blue-3;
	border-bottom: 1px solid $brand-color-sky-blue-6;
	z-index: 99;
}

// Label
.Toolbar-label {
	display: none;
	color: $color-text-muted;
	@include text(2);
	text-transform: uppercase;
	white-space: nowrap;
	@include inset-md;
	padding-right: 0;
	[dir='rtl'] & {
		padding-right: $space-md;
		padding-left: 0;
	}
	a {
		@include link($color-link, true, false);
		.Icon {
			text-decoration: none;
		}
	}
	@include breakpoint(medium) {
		display: flex;
		align-items: center;
	}
}

// Item
.Toolbar-item {
	display: flex;
	flex: 0 1 auto;
	&--alignCenter {
		align-items: center;
	}
	&--divider {
		&::after {
			content: '';
			background-color: $brand-color-sky-blue-6;
			align-self: center;
			width: 1px;
			height: $space-rel-md;
		}
	}
	&--divider-md {
		@include breakpoint(medium) {
			&::after {
				content: '';
				background-color: $brand-color-sky-blue-6;
				align-self: center;
				width: 1px;
				height: $space-rel-md;
			}
		}
	}
	&--fill {
		flex: 1 1 auto;
	}
	&--fill-md {
		@include breakpoint(medium) {
			flex: 1 1 auto;
		}
	}
	&--fill-sm-only {
		flex: 1 1 auto;
		@include breakpoint(medium) {
			width: auto;
			flex: 0 1 auto;
		}
	}
	&--hasDropdown {
		@include breakpoint(medium) {
			position: relative;
		}
		.Toolbar-item-button {
			width: 100%;
			@include inset-md;
			.Icon--triangleDown {
				margin-left: auto;
				[dir='rtl'] & {
					margin-right: auto;
					margin-left: 0;
				}
			}
			@include breakpoint(medium) {
				position: relative;
				width: auto;
				flex: 0 1 auto;
			}
		}
	}
	&--hasDropdownFill {
		@include breakpoint(medium) {
			position: initial;
		}
	}
}

.Toolbar-item > .Suggest {
	@include inset-sm;
	margin: 0;
	align-self: center;
	width: 100%;
	max-width: 100%;
}

.Toolbar-item--hideLabel .Toolbar-item-label {
	@include breakpoint(medium, down) {
		@include visuallyHidden;
	}
}

// Button - child of item
.Toolbar-item-button {
	display: inline-flex;
	align-items: center;
	font-weight: normal;
	&:not(.Button) {
		color: $color-text-muted;
	}
	@include inset-md;
	.Icon {
		flex: 0 0 auto;
		@include icon-sm;
		transition: transform 400ms ease;
	}
	.Icon,
	span {
		display: inline-block;
		&:not(:last-child) {
			margin: 0 0.5em 0 0;
			[dir='rtl'] & {
				margin: 0 0 0 0.5em;
			}
		}
	}
	&--label-md {
		span {
			display: none;
		}
		.Icon {
			margin: 0 !important;
		}
		@include breakpoint(medium) {
			span {
				display: inline-block;
			}
			.Icon {
				&:not(:last-child) {
					margin: 0 0.5em 0 0 !important;
					[dir='rtl'] & {
						margin: 0 0 0 0.5em !important;
					}
				}
			}
		}
	}
	&--label-lg {
		span {
			display: none;
		}
		.Icon {
			margin: 0 !important;
		}
		@include breakpoint(large) {
			span {
				display: inline-block;
			}
			.Icon {
				&:not(:last-child) {
					margin: 0 0.5em 0 0 !important;
					[dir='rtl'] & {
						margin: 0 0 0 0.5em !important;
					}
				}
			}
		}
	}
	&--labelNoWrap {
		span {
			white-space: nowrap;
		}
	}
	&[data-toolbar-toggler-expanded='true'] {
		.Icon--triangleRight,
		.Icon--more {
			transform: rotate(90deg);
		}
	}
}

a.Toolbar-item-button:not(.Button),
button.Toolbar-item-button:not(.Button) {
	@include link($color-link, true, false);
	// @include interactiveColor(color, $color-black, $color-link-hover);
	@include interactiveColor(text-decoration-color, transparent, $color-link-hover);
}

// Button that controls dropdown container
.Toolbar-item-button--hasDropdown {
	width: 100%;
	// flex: 1 1 auto;
	@include inset-md;
	.Icon--triangleDown {
		margin-left: auto;
		[dir='rtl'] & {
			margin-right: auto;
			margin-left: 0;
		}
	}
	@include breakpoint(medium) {
		@include inset-sm;
		position: relative;
		width: auto;
		flex: 0 1 auto;
	}
}

// Dropdown container
.Toolbar-item-dropdown {
	background: $color-white;
	@include dropShadow(2);
	display: none;
	z-index: 100;
	position: absolute;
	@include inset-sm;
	left: 0;
	right: 0;
	top: 100%;
	max-height: 100vh;
	overflow-y: auto;
	@include breakpoint(medium) {
		position: absolute;
		@include inset-squish-md;
		left: 0;
		right: auto;
		[dir='rtl'] & {
			left: auto;
			right: 0;
		}
		top: 100%;
		min-width: 30rem;
		max-height: none;
	}

	&[aria-hidden='false'] {
		display: block;
	}

	&--right {
		@include breakpoint(medium) {
			left: auto;
			right: 0;
			[dir='rtl'] & {
				left: 0;
				right: auto;
			}
		}
	}

	&--orderFirstForSmall {
		order: -1;
		@include breakpoint(medium) {
			order: unset;
		}
	}

	&--fill {
		@include breakpoint(medium) {
			left: 0 !important;
			right: 0 !important;
		}
	}

	&--reverse {
		background: $color-neutral-6;
		@include colorReverse;
	}

	&--overflow {
		overflow-y: visible;
	}
}

// Group
.Toolbar-group {
	flex: 0 1 auto;
	display: flex;
	align-items: stretch;
	@include breakpoint(medium) {
		width: auto;
		flex: 0 1 auto;
	}
	&--wrap {
		flex-wrap: wrap;
	}
	&[data-toolbar-togglegroup-hidden='true'] {
		@include visuallyHidden;
		&:focus-within {
			@include overrideVisuallyHidden;
			display: flex;
		}
	}
}

.Toolbar-group--divider {
	border-right: 1px solid $brand-color-sky-blue-6;
}

.Toolbar-group--divider-md {
	@include breakpoint(medium) {
		border-right: 1px solid $brand-color-sky-blue-6;
	}
}

.Toolbar-group--fill {
	flex: 1 1 auto;
}

.Toolbar-group--fill-md {
	flex: 1 1 auto;
}

.Toolbar-group--fill-sm-only {
	flex: 1 1 auto;
	@include breakpoint(medium) {
		flex: 0 1 auto;
	}
}

.Toolbar-group--right {
	margin-left: auto;
	[dir='rtl'] & {
		margin-right: auto;
		margin-left: 0;
	}
	justify-content: flex-end;
}

.Toolbar-group--fixedToBottom {
	position: fixed;
	bottom: 0;
	left: 0;
	right: 0;
	width: 100%;
	background-color: $brand-color-sky-blue-3;
	@include breakpoint(medium) {
		position: relative;
		bottom: auto;
		left: auto;
		right: auto;
		width: auto;
		background-color: transparent;
	}
	.Toolbar-item--hasDropdown {
		.Toolbar-item-button {
			span:after {
				content: '\2026';
			}
			.Icon--triangleDown {
				display: none;
			}
			@include breakpoint(medium) {
				span:after {
					content: '';
				}
				.Icon--triangleDown {
					display: block;
				}
			}
		}
		.Toolbar-item-dropdown {
			position: fixed;
			top: 0;
			@include breakpoint(medium) {
				position: absolute;
				top: 100%;
			}
		}
	}
}

/* -----------------------
	Variants
------------------------ */

// Reverse mixin
@mixin toolbarReverse {
	background-color: $brand-color-sand-dark-2;
	border-bottom: 0;
	.Toolbar-label {
		color: $color-text-muted-reverse;
	}
	.Toolbar-item--divider {
		&::after {
			background-color: subdueColor($brand-color-sand-dark-6);
		}
	}
	.Toolbar-item--divider-md {
		@include breakpoint(medium) {
			&::after {
				background-color: subdueColor($brand-color-sand-dark-6);
			}
		}
	}
	.Toolbar-item-button:not(.Button) {
		color: $color-text-muted-reverse;
	}
	a.Toolbar-item-button:not(.Button),
	button.Toolbar-item-button:not(.Button) {
		@include link($color-link-reverse, true, true);
		@include interactiveColor(text-decoration-color, transparent, $color-link-hover-reverse);
	}
	.Toolbar-item-dropdown a {
		@include interactiveColor(color, $color-link, $color-link-hover);
		padding: 0;
	}
	.Toolbar-item-dropdown--reverse a {
		@include interactiveColor(color, $color-link-reverse, $color-link-hover-reverse);
		padding: 0;
	}
	.Toolbar-group--divider {
		border-color: subdueColor($brand-color-sand-dark-6);
	}
	.Toolbar-group--divider-md {
		@include breakpoint(medium) {
			border-color: subdueColor($brand-color-sand-dark-6);
		}
	}
	.Toolbar-group--fixedToBottom {
		background-color: $brand-color-sand-dark-2;
		@include breakpoint(medium) {
			background-color: transparent;
		}
	}
}

// Reverse primary dividers mixin
@mixin toolbarReversePrimaryDividers {
	.Toolbar-item--divider {
		&::after {
			background-color: subdueColor($brand-color-ocean-blue-dark-6);
		}
	}
	.Toolbar-item--divider-md {
		@include breakpoint(medium) {
			&::after {
				background-color: subdueColor($brand-color-ocean-blue-dark-6);
			}
		}
	}
	.Toolbar-group--divider {
		border-color: subdueColor($brand-color-ocean-blue-dark-6);
	}

	.Toolbar-group--divider-md {
		@include breakpoint(medium) {
			border-color: subdueColor($brand-color-ocean-blue-dark-6);
		}
	}
}

// Reverse variant - black background
.Toolbar--reverse {
	@include toolbarReverse;
}

// Reverse neutral variant - color neutral 5
.Toolbar--reverseNeutral {
	@include toolbarReverse;
	background-color: $brand-color-sand-dark-2;
}

// Reverse neutral dark variant - color neutral 6
.Toolbar--reverseNeutralDark {
	@include toolbarReverse;
	background-color: $brand-color-sand-dark-1;
	.Toolbar-group--fixedToBottom {
		background-color: $brand-color-sand-dark-1;
		@include breakpoint(medium) {
			background-color: transparent;
		}
	}
}

// Reverse primary variant - color primary 5
.Toolbar--reversePrimary {
	@include toolbarReverse;
	@include toolbarReversePrimaryDividers;
	background-color: $brand-color-ocean-blue-dark-2;
	.Toolbar-group--fixedToBottom {
		background-color: $brand-color-ocean-blue-dark-2;
		@include breakpoint(medium) {
			background-color: transparent;
		}
	}
}

// Reverse primary dark variant - color primary 6
.Toolbar--reversePrimaryDark {
	@include toolbarReverse;
	@include toolbarReversePrimaryDividers;
	background-color: $brand-color-ocean-blue-dark-1;
	.Toolbar-group--fixedToBottom {
		background-color: $brand-color-ocean-blue-dark-1;
		@include breakpoint(medium) {
			background-color: transparent;
		}
	}
}

// one shade lighter than normal Toolbar--verse
.Toolbar--reversePrimary5 {
	@include toolbarReverse;
	@include toolbarReversePrimaryDividers;
	background-color: $brand-color-ocean-blue-dark-2;
	.Toolbar-group--fixedToBottom {
		background-color: $brand-color-ocean-blue-dark-2;
		@include breakpoint(medium) {
			background-color: transparent;
		}
	}
}

// Fixed variant - makes toolbar sticky at the top
.Toolbar--fixed {
	position: sticky;
	top: 0;
}

// Wrap variant - lets toolbar groups wrap
.Toolbar--wrap {
	flex-wrap: wrap;
}

// Small vertical variant - makes toolbar vertical for small screens
.Toolbar,
.Toolbar-group {
	&--sm-vertical {
		flex-direction: column;
		@include breakpoint(medium) {
			flex-direction: row;
		}
	}
}
</style>
